import { produceFormattedChannel, type LiveChannel } from '$/channels'
import { model as config, remote } from '@/config'
import {
  contentTypesWithEpg,
  ONE_MINUTE_IN_SEC,
  ONE_SECOND_MS,
} from '@/constants'
import { MediaUrl } from '@/types'
import { api } from '@setplex/tria-api'
import {
  attach,
  createEvent,
  createStore,
  sample,
  split,
  Store,
  type EventCallable,
} from 'effector'
import { or } from 'patronum'
import { model as session } from '~/entities/session'
import { googlePalFeatureModel } from '~/features/google-pal'
import { exists, getMacrosHeader, T } from '~/shared/helpers'
import { $watchId, generateWatchIdFx } from '~/widgets/player/analytics'

export const loadChannel: EventCallable<{ id: number }> = createEvent()

export const loadChannelUrl: EventCallable<{
  id: number
  startTime: number
  rewind: number
  isRewind?: boolean
}> = createEvent()

export const setCatchupTimeInterval = createEvent<{
  startTime: number
  endTime: number
} | null>()

export const resetCatchupTimeInterval = createEvent()

export const $catchupInterval = createStore<{
  startTime: number
  endTime: number
} | null>(null)
  .on(setCatchupTimeInterval, (_, timeInterval) => timeInterval)
  .reset(resetCatchupTimeInterval)

export const setTimeshift = createEvent<{
  enabled: boolean
  shift: number
  id: number
} | null>()
export const resetTimeshift = createEvent()
export const $timeshift = createStore<{
  enabled: boolean
  shift: number
  id: number
} | null>(null)
  .on(setTimeshift, (_, timeshift) => {
    if (!timeshift) return null

    const { enabled, shift } = timeshift
    const userTimezoneOffset = -new Date().getTimezoneOffset()

    const timeshiftTime = (shift - userTimezoneOffset) * ONE_MINUTE_IN_SEC

    if (!enabled) return { ...timeshift, shift: 0 }

    return { ...timeshift, shift: timeshiftTime }
  })
  .reset(resetTimeshift)

//
export const getOneFx: typeof api.live.base.getOneFx = attach({
  effect: api.live.base.getOneFx,
})
const getOnePlaybackUrlFx: typeof api.live.base.getOnePlaybackUrlFx = attach({
  effect: api.live.base.getOnePlaybackUrlFx,
})

const getCatchupPlaybackUrlFx: typeof api.live.base.getCatchupPlaybackUrlFx =
  attach({
    effect: api.live.base.getCatchupPlaybackUrlFx,
  })

// Channel
export const $channel = createStore<LiveChannel | null>(null)
  .reset(getOneFx, getOneFx.fail)
  .on(getOneFx.doneData, (_, channel) => produceFormattedChannel({})(channel))

sample({
  clock: $channel,
  source: $timeshift,
  filter: (timeshift, channel) => Boolean(channel) && !timeshift,
  fn: (_, channel) => ({ ...channel?.timeshift, id: channel?.id }),
  target: setTimeshift,
})

export const $url = createStore<MediaUrl | null>(null)
  .reset(
    getOnePlaybackUrlFx,
    getOnePlaybackUrlFx.fail,
    getCatchupPlaybackUrlFx,
    getCatchupPlaybackUrlFx.fail
  )
  .on(
    [getOnePlaybackUrlFx.doneData, getCatchupPlaybackUrlFx.doneData],
    (_, { playbackUrl }) => (playbackUrl ? { playbackUrl } : null)
  )

export const $isUrlRequestPending: Store<boolean> = or(
  getOnePlaybackUrlFx.pending,
  getCatchupPlaybackUrlFx.pending
)

export const $isUrlRequestFailed: Store<boolean> = createStore(false)
  .on([getOnePlaybackUrlFx.fail, getCatchupPlaybackUrlFx.fail], T)
  .reset([getCatchupPlaybackUrlFx, getOnePlaybackUrlFx])

const resetLoadChannelUrlParams = createEvent()
const $loadChannelUrlParams = createStore<{
  id: number
  startTime: number
  rewind: number
  isRewind?: boolean
} | null>(null)
  .on(loadChannelUrl, (_, data) => data)
  .reset(resetLoadChannelUrlParams)

sample({
  clock: googlePalFeatureModel.$idType,
  source: { channel: $channel, url: $url },
  fn: ({ channel, url }, idType) => {
    //should not reset url if non-live content
    if (idType?.type !== contentTypesWithEpg.liveChannel) return url

    if (!channel || !idType || channel.id !== idType.id) return null

    return url
  },
  target: $url,
})

sample({
  clock: loadChannel,
  target: getOneFx,
})

// get rewind url
sample({
  clock: getOnePlaybackUrlFx.doneData,
  source: { requested: $loadChannelUrlParams, activeChannel: $channel },
  fn: ({ requested, activeChannel }, url) => {
    if (
      exists(requested?.startTime) &&
      requested.isRewind &&
      activeChannel?.id === requested.id
    ) {
      return {
        ...url,
        timeShiftSec: Math.round(requested.startTime / ONE_SECOND_MS),
      }
    }
    return url
  },
  target: [$url, resetLoadChannelUrlParams],
})

const doRequest: EventCallable<void> = createEvent<void>()

// Load content after watchId is generated
sample({
  clock: generateWatchIdFx.done,
  source: {
    idType: googlePalFeatureModel.$idType,
  },
  filter: ({ idType }) => idType?.type === contentTypesWithEpg.liveChannel,
  target: doRequest,
})

const getLiveUrl = createEvent()
const getCatchupUrl = createEvent()

split({
  clock: doRequest,
  source: $catchupInterval,
  match: {
    getLiveUrl: (catchupInterval) => !catchupInterval,
    getCatchupUrl: (catchupInterval) => Boolean(catchupInterval),
  },
  cases: {
    getLiveUrl,
    getCatchupUrl,
  },
})

sample({
  clock: getLiveUrl,
  source: {
    idType: googlePalFeatureModel.$idType,
    nonce: googlePalFeatureModel.$nonce,
    macrosHeader: config.get(remote.uvo_macrosHeader),
    requestRandom: $watchId,
    loadChannelUrlParams: $loadChannelUrlParams,
    timeshift: $timeshift,
    sessionInfo: session.$info,
  },
  fn: ({
    idType,
    nonce,
    macrosHeader,
    requestRandom,
    loadChannelUrlParams,
    timeshift,
    sessionInfo,
  }) => {
    const userGuid = sessionInfo?.userGuid
    let headers = {}
    if (macrosHeader && requestRandom && userGuid) {
      headers = {
        [macrosHeader]: getMacrosHeader(requestRandom, userGuid, nonce),
      }
    }

    const isRewind = loadChannelUrlParams?.isRewind

    if (isRewind) {
      const startTime = loadChannelUrlParams.startTime
      const rewind = loadChannelUrlParams.rewind
      return { id: idType?.id, headers, startTime, rewind, isRewind }
    }

    const isTimeshift = timeshift?.enabled

    if (isTimeshift) {
      return { id: idType?.id, headers, timeshift: timeshift?.shift }
    }

    return { id: idType?.id, headers }
  },
  target: getOnePlaybackUrlFx,
})

sample({
  clock: getCatchupUrl,
  source: {
    idType: googlePalFeatureModel.$idType,
    nonce: googlePalFeatureModel.$nonce,
    macrosHeader: config.get(remote.uvo_macrosHeader),
    catchupInterval: $catchupInterval,
    requestRandom: $watchId,
    sessionInfo: session.$info,
  },
  fn: ({
    idType,
    nonce,
    macrosHeader,
    catchupInterval,
    requestRandom,
    sessionInfo,
  }) => {
    const userGuid = sessionInfo?.userGuid
    let headers = {}
    if (macrosHeader && requestRandom && userGuid) {
      headers = {
        [macrosHeader]: getMacrosHeader(requestRandom, userGuid, nonce),
      }
    }
    const { startTime, endTime } = catchupInterval || {}
    return { id: idType?.id, headers, startTime, endTime }
  },
  target: getCatchupPlaybackUrlFx,
})
